/**
 * The fields a CSV column can be matched to, per record type. The keys are the row keys the
 * backend import DTOs accept (`csv-import-contacts.dto.ts`, `csv-import-companies.dto.ts`,
 * `csv-candidate-import.ts`); labels and hints live under `csv_import.fields` / `csv_import.hints`.
 *
 * `aliases` are other headings the same column commonly carries in exports from other systems, so
 * the mapping step can pre-select it. They are compared with punctuation, spacing and case removed.
 */

export type ImportEntity = 'contact' | 'company' | 'candidate';

export type ImportFieldGroup =
  | 'identity'
  | 'details'
  | 'company'
  | 'address'
  | 'availability'
  | 'organisation'
  | 'links'
  | 'advanced';

export interface ImportField {
  /** The row key sent to the backend. */
  key: string;
  group: ImportFieldGroup;
  /** Defaults to `key`; set where one key means different things per record type. */
  labelKey?: string;
  aliases?: string[];
  /** Whether `csv_import.hints.<labelKey>` exists for this field. */
  hint?: boolean;
  /** Example cell for the downloadable template. */
  example?: string;
  /** Left out of the downloadable template (still matchable). */
  hiddenFromTemplate?: boolean;
}

/**
 * A rule the mapping must satisfy before import. It is met when every field of ANY one of its
 * alternatives is matched — e.g. first and last name, or a single full name column.
 */
export interface ImportRequirement {
  alternatives: string[][];
}

export interface ImportEntityConfig {
  fields: ImportField[];
  requirements: ImportRequirement[];
}

/** A combined name column, split into first and last name before the rows are sent. */
export const FULL_NAME_KEY = 'full_name';

const NAME_FIELDS: ImportField[] = [
  { key: 'first_name', group: 'identity', aliases: ['First', 'Given name', 'Forename'], example: 'Jane' },
  { key: 'last_name', group: 'identity', aliases: ['Last', 'Surname', 'Family name'], example: 'Smith' },
  { key: FULL_NAME_KEY, group: 'identity', aliases: ['Name', 'Contact name', 'Candidate name'], hint: true, hiddenFromTemplate: true },
  { key: 'email', group: 'identity', aliases: ['Email address', 'E-mail', 'Work email', 'Primary email'], example: 'jane.smith@example.com' },
];

/** "LinkedIn Link" and the like are the headings of the templates this importer used to ship. */
const PERSON_LINK_FIELDS: ImportField[] = [
  { key: 'linkedin_link', group: 'links', aliases: ['LinkedIn Link', 'LinkedIn URL', 'LinkedIn profile'], example: 'https://linkedin.com/in/janesmith' },
  { key: 'x_link', group: 'links', aliases: ['X Link', 'X', 'Twitter', 'Twitter URL'] },
  { key: 'facebook_link', group: 'links', aliases: ['Facebook Link'] },
  { key: 'github_link', group: 'links', aliases: ['Github Link'] },
  { key: 'website_link', group: 'links', aliases: ['Personal website', 'Portfolio'] },
];

const NAME_REQUIREMENTS: ImportRequirement[] = [
  { alternatives: [['first_name', 'last_name'], [FULL_NAME_KEY]] },
  { alternatives: [['email']] },
];

export const IMPORT_ENTITY_CONFIGS: Record<ImportEntity, ImportEntityConfig> = {
  contact: {
    fields: [
      ...NAME_FIELDS,
      { key: 'title', group: 'details', labelKey: 'salutation', aliases: ['Salutation', 'Prefix'], example: 'Ms' },
      { key: 'position_title', group: 'details', aliases: ['Job title', 'Title', 'Position', 'Role'], example: 'Head of Talent' },
      { key: 'phone_number', group: 'details', aliases: ['Phone', 'Mobile', 'Mobile phone', 'Mobile number', 'Telephone', 'Work phone', 'Cell'], example: '+61400000000' },
      { key: 'location', group: 'details', aliases: ['City', 'Suburb'], example: 'Sydney NSW' },
      { key: 'contact_type', group: 'details', aliases: ['Type'], hint: true, example: 'Hiring Manager' },
      { key: 'special_information', group: 'details', aliases: ['Notes', 'Note', 'Comments'] },
      { key: 'company_name', group: 'company', aliases: ['Company', 'Organisation', 'Organization', 'Employer', 'Account', 'Account name', 'Business name'], hint: true, example: 'Acme Pty Ltd' },
      { key: 'company_website', group: 'company', hint: true, example: 'https://acme.com.au' },
      { key: 'company_phone', group: 'company', aliases: ['Office phone', 'Main phone'], hint: true },
      { key: 'owner_email', group: 'organisation', aliases: ['Owner', 'Account owner', 'Assigned to'], hint: true },
      ...PERSON_LINK_FIELDS,
      { key: 'company_id', group: 'advanced', hint: true, hiddenFromTemplate: true },
      { key: 'owner_id', group: 'advanced', hint: true, hiddenFromTemplate: true },
    ],
    requirements: NAME_REQUIREMENTS,
  },

  company: {
    fields: [
      { key: 'company_name', group: 'identity', aliases: ['Name', 'Company', 'Organisation', 'Organization', 'Account name', 'Business name'], example: 'Acme Pty Ltd' },
      { key: 'company_information', group: 'details', aliases: ['Description', 'About', 'Information', 'Notes'], example: 'Mining services provider' },
      { key: 'company_type', group: 'details', aliases: ['Type'], hint: true, example: 'Client' },
      { key: 'company_website', group: 'details', aliases: ['Website', 'Web', 'URL', 'Domain'], example: 'https://acme.com.au' },
      { key: 'company_phone', group: 'details', aliases: ['Phone', 'Office phone', 'Main phone', 'Telephone'], example: '+61290001234' },
      { key: 'company_size', group: 'details', aliases: ['Size', 'Employees', 'Headcount', 'Staff'], hint: true, example: '51-100' },
      { key: 'tax_id', group: 'details', aliases: ['ABN', 'ACN', 'Tax ID', 'VAT number', 'EIN', 'Company number', 'Registration number'] },
      { key: 'street', group: 'address', aliases: ['Address', 'Street address', 'Address line 1'], example: '1 George St' },
      { key: 'city', group: 'address', aliases: ['Suburb', 'Town'], example: 'Sydney' },
      { key: 'state', group: 'address', aliases: ['Region', 'Province'], example: 'NSW' },
      { key: 'country_code', group: 'address', aliases: ['Country'], hint: true, example: 'AU' },
      { key: 'owner_email', group: 'organisation', aliases: ['Owner', 'Account owner', 'Assigned to'], hint: true },
      { key: 'labels', group: 'organisation', aliases: ['Label'], hint: true },
      { key: 'linkedin_link', group: 'links', aliases: ['LinkedIn Link', 'LinkedIn URL'], example: 'https://linkedin.com/company/acme' },
      { key: 'x_link', group: 'links', aliases: ['X Link', 'X', 'Twitter'] },
      { key: 'facebook_link', group: 'links', aliases: ['Facebook Link'] },
      { key: 'github_link', group: 'links', aliases: ['Github Link'] },
    ],
    requirements: [{ alternatives: [['company_name']] }],
  },

  candidate: {
    fields: [
      ...NAME_FIELDS,
      { key: 'position_title', group: 'details', aliases: ['Job title', 'Title', 'Position', 'Current title', 'Role'], example: 'Senior Data Engineer' },
      { key: 'phone_number', group: 'details', aliases: ['Phone', 'Mobile', 'Mobile phone', 'Mobile number', 'Telephone', 'Cell'], example: '0400000000' },
      { key: 'country_code', group: 'details', labelKey: 'phone_country_code', aliases: ['Country code', 'Dialling code', 'Dial code'], hint: true, example: '+61' },
      { key: 'location', group: 'details', aliases: ['City', 'Suburb'], example: 'Newcastle NSW' },
      { key: 'summary', group: 'details', aliases: ['Profile summary', 'Bio', 'About', 'Overview'] },
      { key: 'special_information', group: 'details', aliases: ['Notes', 'Note', 'Comments'] },
      { key: 'availability_status', group: 'availability', aliases: ['Availability'], hint: true, example: 'Available Soon' },
      { key: 'available_from', group: 'availability', aliases: ['Available date', 'Availability date'], hint: true, example: '01/10/2026' },
      { key: 'work_type', group: 'availability', aliases: ['Job type', 'Employment type', 'Type'], hint: true, example: 'Full time' },
      { key: 'salary_min', group: 'availability', aliases: ['Salary', 'Minimum salary', 'Min salary', 'Expected salary', 'Desired salary'], example: '120000' },
      { key: 'salary_max', group: 'availability', aliases: ['Maximum salary', 'Max salary'], example: '140000' },
      { key: 'salary_period', group: 'availability', aliases: ['Pay period', 'Rate period'], hint: true, example: 'Year' },
      { key: 'source', group: 'organisation', aliases: ['Lead source', 'Candidate source'], hint: true },
      { key: 'rating', group: 'organisation', aliases: ['Stars', 'Score'], hint: true, example: '4' },
      { key: 'labels', group: 'organisation', aliases: ['Label'], hint: true },
      { key: 'tags', group: 'organisation', aliases: ['Keywords', 'Skills'], hint: true },
      ...PERSON_LINK_FIELDS,
    ],
    requirements: NAME_REQUIREMENTS,
  },
};

export const fieldLabelKey = (field: ImportField): string => field.labelKey ?? field.key;
